import MoreVertIcon from '@mui/icons-material/MoreVert';
import Grid from '@mui/material/Grid';
import Menu from '@mui/material/Menu';
import { styled } from '@mui/material/styles';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { palette } from '@openx/components-core/lib/theme/paletteV2.ts';
import { type JSX, useCallback, useMemo, useRef, useState } from 'react';
import { MenuButtonItem } from './MenuButtonItem.tsx';

const StyledButton = styled(Button)`
  min-width: initial;
  width: 32px;
  background-color: ${palette.background.boxDark};
  &:hover {
    background-color: ${palette.background.boxDarkest};
  }
  svg {
    color: ${palette.text.secondary};
    font-size: 20px;
  }
`;

export const MenuButton = ({ actions, position = 4 }) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const handleClose = useCallback((): void => {
    setOpen(false);
  }, []);

  const handleToggle = (): void => {
    setOpen(prevOpen => !prevOpen);
  };

  const MenuItems = useMemo(
    (): JSX.Element => (
      <div data-test="actions-sub-menu">
        {actions.map((action, index) => {
          const isItemAllowed = action.isAllowed ?? true;
          const isItemDisabled = action.disabled || action.loading || !isItemAllowed;
          const isAllowedMessageString = !isItemAllowed
            ? action.notAllowedMessage || 'You need permission to perform this action'
            : '';

          return (
            <MenuButtonItem
              disabled={isItemDisabled}
              danger={action.color === 'error'}
              data-test={action['data-test']}
              isAllowedMessage={isAllowedMessageString}
              key={index}
              label={action.label}
              onClick={e => {
                action.onClick(e);
                handleClose();
              }}
            ></MenuButtonItem>
          );
        })}
      </div>
    ),
    [actions, handleClose]
  );

  return (
    <Grid ref={anchorRef} sx={{ order: position }}>
      <StyledButton
        aria-label="Page actions menu"
        data-test="menu-action-button"
        onClick={() => {
          handleToggle();
        }}
      >
        <MoreVertIcon />
      </StyledButton>
      <Menu
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        sx={{
          marginTop: '5px',
        }}
        slotProps={{
          list: { disablePadding: true },
        }}
      >
        {MenuItems}
      </Menu>
    </Grid>
  );
};
